// Scale bridge dashboard - every weighing, newest first.
//
// This is also the accessible alternative to the Overview chart: the same numbers
// as text, sortable and searchable. A chart without a table behind it asks the
// reader to estimate values off a bar.
//
// ─── The toolbar, and why the card lost its title ──────────────────────
// The card used to be headed "Weighings" directly under a header that also said
// "Weighings". One of the two was furniture. The header keeps the title; the
// space it freed goes to the row that says what is being shown and lets you
// narrow it - which is the thing an operator actually uses.

import { useMemo, useState } from 'react';
import { Button, Card, Empty, Input, Select, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

import StatusTag from '../components/StatusTag';
import Value from '../components/Value';
import ReadingDetailDrawer from '../components/ReadingDetailDrawer';
import { clockTime, dayLabel, timestamp, weightKg } from '../lib/format';
import { useReadingsStore } from '../state/readingsStore';
import { useAuthStore } from '../state/authStore';
import { getScrollContainer } from '../nav';
import type { Reading } from '../types';

const { Text } = Typography;

const ALL_DEVICES = '__all__';

export default function Readings() {
    const readings = useReadingsStore((s) => s.readings);
    const loaded = useReadingsStore((s) => s.loaded);
    const session = useAuthStore((s) => s.session)!;

    const [query, setQuery] = useState('');
    const [device, setDevice] = useState<string>(ALL_DEVICES);
    const [selected, setSelected] = useState<Reading | null>(null);

    // Taken from the rows on screen rather than from the fleet endpoint: this
    // filter narrows what is in the table, so offering a device with nothing in
    // the table would be an option that always yields an empty result.
    const deviceIds = useMemo(
        () => Array.from(new Set(readings.map((r) => r.deviceId))).sort(),
        [readings],
    );

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return readings.filter((r) => {
            if (device !== ALL_DEVICES && r.deviceId !== device) return false;
            if (!q) return true;
            // Searches the fields somebody would actually have written down: the
            // barcode off the label, a batch number, or the bill id from a phone.
            return [r.id, r.barcode, r.batchNo, r.date, r.deviceId, r.billNo ?? '', r.itemName ?? '']
                .some((v) => (v ?? '').toLowerCase().includes(q));
        });
    }, [readings, query, device]);

    const narrowed = query.trim() !== '' || device !== ALL_DEVICES;

    function clearFilters() {
        setQuery('');
        setDevice(ALL_DEVICES);
    }

    const columns: ColumnsType<Reading> = [
        {
            title: 'Received',
            dataIndex: 'receivedAt',
            width: 150,
            defaultSortOrder: 'descend',
            sorter: (a, b) => a.receivedAt.localeCompare(b.receivedAt),
            render: (v: string) => (
                // Time over date. Nearly every row is from today, so the date is
                // the part that repeats and the clock time is the part that
                // identifies the row - see clockTime/dayLabel in lib/format.
                <Tooltip title={timestamp(v)}>
                    <div style={{ lineHeight: 1.35 }}>
                        <div className="num" style={{ fontWeight: 550 }}>
                            {clockTime(v)}
                        </div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {dayLabel(v)}
                        </Text>
                    </div>
                </Tooltip>
            ),
        },
        {
            title: 'Net weight',
            dataIndex: 'totalNetWeightKg',
            width: 150,
            align: 'right',
            sorter: (a, b) => (a.totalNetWeightKg ?? -1) - (b.totalNetWeightKg ?? -1),
            render: (v: number | null, row) => (
                <div style={{ lineHeight: 1.35 }}>
                    <span className="num" style={{ fontWeight: 600 }}>{weightKg(v)}</span>
                    {/* Never shown as 0. When the parse failed the raw text is the
                        honest thing to display. */}
                    {v === null && (
                        <div>
                            <Text type="danger" style={{ fontSize: 11 }} className="mono">
                                label said <Value value={row.totalNetWeightRaw} />
                            </Text>
                        </div>
                    )}
                </div>
            ),
        },
        {
            title: 'Items',
            dataIndex: 'items',
            width: 100,
            align: 'right',
            render: (items: Reading['items'], row) => (
                <Space size={4}>
                    <span className="num">{items.length}</span>
                    {row.itemsTruncated && (
                        // "cut" was too terse to act on. The word says what happened
                        // and the tooltip says what it costs.
                        <Tooltip title="The label carried more item rows than the device stores, so this list is incomplete.">
                            <Tag color="error" style={{ marginInlineEnd: 0 }}>
                                Truncated
                            </Tag>
                        </Tooltip>
                    )}
                </Space>
            ),
        },
        {
            title: 'Item',
            dataIndex: 'itemName',
            width: 130,
            ellipsis: true,
            render: (v: string | null | undefined) => <Value value={v} />,
        },
        {
            title: 'Bill no.',
            dataIndex: 'billNo',
            width: 110,
            align: 'right',
            render: (v: string | null | undefined) => <Value value={v} mono num />,
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            width: 110,
            align: 'right',
            sorter: (a, b) => (a.amount ?? -1) - (b.amount ?? -1),
            // Absent is shown as a dash, never as 0.00 - see the note in types.ts.
            render: (v: number | null | undefined) =>
                typeof v === 'number' ? (
                    <span className="num">{v.toFixed(2)}</span>
                ) : (
                    <Value value={null} />
                ),
        },
        {
            title: 'Batch no.',
            dataIndex: 'batchNo',
            width: 130,
            render: (v: string | null) => <Value value={v} />,
        },
        {
            title: 'Date on label',
            dataIndex: 'date',
            width: 130,
            render: (v: string | null) => <Value value={v} />,
        },
        {
            title: 'Testing',
            dataIndex: 'testing',
            width: 130,
            render: (v: string | null) => <StatusTag value={v} />,
        },
        {
            title: 'Barcode',
            dataIndex: 'barcode',
            width: 140,
            render: (v: string | null) => <Value value={v} mono />,
        },
    ];

    return (
        <>
            <Card styles={{ body: { paddingTop: 16 } }}>
                <div className="sb-toolbar">
                    <div className="sb-toolbar__left">
                        <Input
                            allowClear
                            prefix={<SearchOutlined style={{ color: 'var(--sb-muted)' }} />}
                            placeholder="Barcode, batch, bill id…"
                            aria-label="Search weighings"
                            style={{ width: 280 }}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                        />

                        {/* Only offered when there is a choice to make. On a
                            single-bridge shop it would be a control with one
                            option, which is a control that does nothing. */}
                        {deviceIds.length > 1 && (
                            <Select
                                value={device}
                                onChange={setDevice}
                                style={{ width: 190 }}
                                aria-label="Filter by device"
                                options={[
                                    { value: ALL_DEVICES, label: 'All devices' },
                                    ...deviceIds.map((id) => ({ value: id, label: id })),
                                ]}
                            />
                        )}

                        {narrowed && (
                            <Button type="link" size="small" onClick={clearFilters}>
                                Clear
                            </Button>
                        )}
                    </div>

                    {/* Says what is on screen against what exists. Without the
                        second half, a filter that hides most of the day looks
                        identical to a quiet day. */}
                    <div className="sb-toolbar__right">
                        <span className="sb-toolbar__count">
                            {narrowed
                                ? `${filtered.length.toLocaleString('en-IN')} of ${readings.length.toLocaleString('en-IN')} weighings`
                                : `${readings.length.toLocaleString('en-IN')} weighing${readings.length === 1 ? '' : 's'}`}
                        </span>
                    </div>
                </div>

                <Table<Reading>
                    rowKey="id"
                    size="middle"
                    columns={columns}
                    dataSource={filtered}
                    loading={!loaded}
                    onRow={(row) => ({
                        onClick: () => setSelected(row),
                        style: { cursor: 'pointer' },
                    })}
                    pagination={{
                        pageSize: 25,
                        showSizeChanger: true,
                        // No showTotal: the count now lives in the toolbar, where
                        // it is visible without scrolling past twenty-five rows.
                        size: 'default',
                    }}
                    // The sum of the column widths above. It was 1000 against
                    // columns totalling 1290, which told the table it had 290px
                    // less to lay out than it does and left the last column
                    // clipped rather than scrollable.
                    scroll={{ x: 1290 }}
                    // The content pane is the scroller, not the window - so the
                    // header has to be told which element to stick inside, or it
                    // pins itself to a viewport that never moves.
                    sticky={{ offsetHeader: 0, getContainer: getScrollContainer }}
                    locale={{
                        emptyText: narrowed ? (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={
                                    <Space direction="vertical" size={4}>
                                        <Text type="secondary">No weighing matches this search</Text>
                                        <Button type="link" size="small" onClick={clearFilters}>
                                            Clear the filters
                                        </Button>
                                    </Space>
                                }
                            />
                        ) : (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={
                                    <Space direction="vertical" size={2}>
                                        <Text type="secondary">No weighings recorded yet</Text>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            Print a label on the weighbridge and it will appear here.
                                        </Text>
                                    </Space>
                                }
                            />
                        ),
                    }}
                />
            </Card>

            <ReadingDetailDrawer
                reading={selected}
                session={session}
                onClose={() => setSelected(null)}
            />
        </>
    );
}
